import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';
import { useToast } from '../hooks/useToast';

export default function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const toast = useToast();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/users');
      if (!res.ok) throw new Error();
      setUsers(await res.json());
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    authFetch('/api/user/me').then(r => r.json()).then(d => setCurrentUserEmail(d.email)).catch(() => {});
  }, []);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Enter an email address'); return; }
    try {
      const res = await authFetch('/api/admin/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const newUser = await res.json();
      setUsers(prev => [{ ...newUser, project_count: 0, is_active: true, admin_permissions: [] }, ...prev]);
      setInviteEmail('');
      toast.success('Invite sent successfully');
    } catch (e) { toast.error(e.message || 'Failed to invite user'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const res = await authFetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (e) { toast.error(e.message || 'Failed to update role'); }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this user? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User removed');
    } catch (e) { toast.error(e.message || 'Failed to remove user'); }
  };

  return (
    <div className="aum-page">
      <style>{CSS}</style>

      {/* Header */}
      <div className="aum-header">
        <h2 className="aum-title">Team Members</h2>
        <p className="aum-desc">Invite teammates and manage roles</p>
      </div>

      {/* Role Badges Explanation */}
      <div className="aum-roles-card">
        <span className="aum-role-badge aum-role-badge--admin">admin</span>
        <span className="aum-role-desc">Full access · connect platforms · manage billing · remove members</span>
        <span className="aum-role-badge aum-role-badge--user">member</span>
        <span className="aum-role-desc">View repos and dashboard · read-only</span>
        <span className="aum-role-badge aum-role-badge--sales">sales</span>
        <span className="aum-role-desc">Blog management and own profile only</span>
      </div>

      {/* Invite Form */}
      <div className="aum-invite-card">
        <h3 className="aum-invite-title">Invite a teammate</h3>
        <div className="aum-invite-form">
          <div className="aum-invite-field">
            <label className="aum-invite-label">EMAIL ADDRESS</label>
            <input
              className="aum-invite-input"
              type="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            />
          </div>
          <div className="aum-invite-field aum-invite-field--role">
            <label className="aum-invite-label">ROLE</label>
            <select className="aum-invite-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
              <option value="user">Member</option>
              <option value="sales">Sales</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button className="aum-invite-btn" onClick={handleInvite}>Send Invite</button>
        </div>
      </div>

      {/* Members Table */}
      {loading ? (
        <div className="aum-loading">Loading users...</div>
      ) : (
        <div className="aum-table-wrap">
          <table className="aum-table">
            <thead>
              <tr>
                <th>EMAIL</th>
                <th>ROLE</th>
                <th>JOINED</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const isMe = user.email === currentUserEmail;
                return (
                  <tr key={user.id}>
                    <td className="aum-email">
                      {user.email}
                      {isMe && <span className="aum-you-badge">you</span>}
                    </td>
                    <td>
                      {isMe ? (
                        <span className="aum-role-text">{user.role}</span>
                      ) : (
                        <select
                          className="aum-role-select"
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        >
                          <option value="admin">admin</option>
                          <option value="user">member</option>
                          <option value="sales">sales</option>
                        </select>
                      )}
                    </td>
                    <td className="aum-date">
                      {user.created_at ? user.created_at.split('T')[0] : '—'}
                    </td>
                    <td>
                      {!isMe && user.role !== 'admin' && (
                        <button className="aum-remove-btn" onClick={() => handleRemove(user.id)}>Remove</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CSS = `
.aum-page { padding: 32px 0; max-width: 1100px; margin: 0 auto; }
.aum-header { margin-bottom: 24px; }
.aum-title {
  font-size: 22px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.2;
  color: rgba(241,245,249,0.97); margin: 0 0 6px;
  font-variant-numeric: tabular-nums;
}
[data-theme="light"] .aum-title { color: #0f172a; }
.aum-desc {
  font-size: 13px; font-weight: 400; line-height: 1.6;
  color: rgba(148,163,184,0.62); margin: 0;
  letter-spacing: 0.01em;
}
[data-theme="light"] .aum-desc { color: #64748b; }
.aum-loading {
  text-align: center; padding: 60px 0; font-size: 13px;
  color: rgba(100,116,139,0.5); letter-spacing: 0.02em;
}

/* ── Role Badges Card ── */
.aum-roles-card {
  position: relative;
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  padding: 14px 20px; border-radius: 18px; margin-bottom: 20px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.aum-roles-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
[data-theme="light"] .aum-roles-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .aum-roles-card::before { opacity: 0.55; }
.aum-role-badge {
  padding: 3px 10px; border-radius: 20px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: lowercase;
}
.aum-role-badge--admin {
  background: rgba(239,68,68,0.09); color: rgba(252,165,165,0.85);
  border: 1px solid rgba(239,68,68,0.2);
}
[data-theme="light"] .aum-role-badge--admin { color: #dc2626; background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.18); }
.aum-role-badge--user {
  background: rgba(99,102,241,0.1); color: rgba(165,180,252,0.88);
  border: 1px solid rgba(99,102,241,0.22);
}
[data-theme="light"] .aum-role-badge--user { color: #6366f1; background: rgba(99,102,241,0.07); border-color: rgba(99,102,241,0.18); }
.aum-role-badge--sales {
  background: rgba(245,158,11,0.09); color: rgba(251,191,36,0.82);
  border: 1px solid rgba(245,158,11,0.2);
}
[data-theme="light"] .aum-role-badge--sales { color: #d97706; background: rgba(245,158,11,0.07); border-color: rgba(245,158,11,0.18); }
.aum-role-desc {
  font-size: 12px; line-height: 1.5; letter-spacing: 0.005em;
  color: rgba(100,116,139,0.6);
}
[data-theme="light"] .aum-role-desc { color: #64748b; }

/* ── Invite Card ── */
.aum-invite-card {
  position: relative;
  padding: 20px 24px; border-radius: 18px; margin-bottom: 20px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.aum-invite-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
[data-theme="light"] .aum-invite-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .aum-invite-card::before { opacity: 0.55; }

.aum-invite-title {
  font-size: 13px; font-weight: 700; letter-spacing: 0.005em;
  color: rgba(226,232,240,0.9); margin: 0 0 14px; position: relative;
}
[data-theme="light"] .aum-invite-title { color: #1e293b; }
.aum-invite-form { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.aum-invite-field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 200px; }
.aum-invite-field--role { flex: 0 0 130px; min-width: 130px; }
.aum-invite-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(99,102,241,0.55);
}
[data-theme="light"] .aum-invite-label { color: rgba(99,102,241,0.5); }
.aum-invite-input {
  padding: 9px 14px; border-radius: 8px; font-size: 13px;
  background: rgba(6,9,22,0.6); border: 1px solid rgba(99,102,241,0.2);
  color: rgba(226,232,240,0.92); outline: none; width: 100%;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.3);
  transition: border-color 0.18s, box-shadow 0.18s;
}
[data-theme="light"] .aum-invite-input { background: rgba(248,250,252,0.92); border-color: rgba(99,102,241,0.16); color: #1e293b; box-shadow: none; }
.aum-invite-input::placeholder { color: rgba(100,116,139,0.55); }
.aum-invite-input:hover:not(:focus) { border-color: rgba(99,102,241,0.32); }
.aum-invite-input:focus { border-color: rgba(99,102,241,0.6); background: rgba(8,12,30,0.72); box-shadow: 0 0 0 3px rgba(99,102,241,0.13), inset 0 1px 2px rgba(0,0,0,0.25); }
[data-theme="light"] .aum-invite-input:focus { border-color: rgba(99,102,241,0.45); background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.aum-invite-select {
  padding: 9px 32px 9px 14px; border-radius: 8px; font-size: 13px;
  background: rgba(6,9,22,0.6); border: 1px solid rgba(99,102,241,0.2);
  color: rgba(226,232,240,0.92); outline: none; cursor: pointer; width: 100%;
  appearance: none; -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(148,163,184,0.6)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 11px center;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.3);
  transition: border-color 0.18s, box-shadow 0.18s;
}
[data-theme="light"] .aum-invite-select {
  background-color: rgba(248,250,252,0.92); border-color: rgba(99,102,241,0.16); color: #1e293b; box-shadow: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(71,85,105,0.55)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
}
.aum-invite-select:hover:not(:focus) { border-color: rgba(99,102,241,0.32); }
.aum-invite-select:focus { border-color: rgba(99,102,241,0.62); background: rgba(8,12,30,0.72); box-shadow: 0 0 0 3px rgba(99,102,241,0.14), inset 0 1px 2px rgba(0,0,0,0.25); }
[data-theme="light"] .aum-invite-select:focus { border-color: rgba(99,102,241,0.45); background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.aum-invite-select option { background: #0f1629; color: rgba(226,232,240,0.92); }
[data-theme="light"] .aum-invite-select option { background: #fff; color: #1e293b; }
.aum-invite-btn {
  padding: 10px 22px; border-radius: 9px; font-size: 13px; font-weight: 600;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border: 1px solid rgba(139,92,246,0.35);
  color: rgba(255,255,255,0.97); cursor: pointer; white-space: nowrap;
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.4),
    0 4px 18px rgba(99,102,241,0.35),
    inset 0 1px 0 rgba(255,255,255,0.18),
    inset 0 -1px 0 rgba(0,0,0,0.12);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  letter-spacing: 0.01em;
}
.aum-invite-btn:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.55),
    0 6px 28px rgba(99,102,241,0.48),
    inset 0 1px 0 rgba(255,255,255,0.2),
    inset 0 -1px 0 rgba(0,0,0,0.14);
  transform: translateY(-1px);
}
.aum-invite-btn:active { transform: translateY(0); box-shadow: 0 0 0 1px rgba(139,92,246,0.4), 0 2px 10px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.12); }
[data-theme="light"] .aum-invite-btn { box-shadow: 0 0 0 1px rgba(99,102,241,0.3), 0 4px 14px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.2); }
[data-theme="light"] .aum-invite-btn:hover { box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 6px 20px rgba(99,102,241,0.32), inset 0 1px 0 rgba(255,255,255,0.22); }

/* ── Table ── */
.aum-table-wrap {
  position: relative;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  border-radius: 18px; overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.aum-table-wrap::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8; z-index: 2;
}
[data-theme="light"] .aum-table-wrap {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .aum-table-wrap::before { opacity: 0.55; }

.aum-table { width: 100%; border-collapse: collapse; position: relative; z-index: 1; }

/* Header row */
.aum-table thead tr {
  background: linear-gradient(90deg, rgba(99,102,241,0.08) 0%, rgba(99,102,241,0.04) 100%);
  border-bottom: 1px solid rgba(99,102,241,0.13);
}
[data-theme="light"] .aum-table thead tr { background: rgba(99,102,241,0.04); border-bottom-color: rgba(99,102,241,0.09); }
.aum-table th {
  text-align: left; padding: 12px 20px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase;
  color: rgba(99,102,241,0.55);
  border-bottom: 1px solid rgba(99,102,241,0.12);
}
[data-theme="light"] .aum-table th {
  color: rgba(99,102,241,0.5);
  border-bottom-color: rgba(99,102,241,0.09);
}
.aum-table th:last-child { text-align: right; }

/* Body rows */
.aum-table td {
  padding: 14px 20px; font-size: 13px; line-height: 1.45;
  color: rgba(203,213,225,0.8);
  border-bottom: 1px solid rgba(99,102,241,0.065);
  vertical-align: middle;
}
[data-theme="light"] .aum-table td { color: #334155; border-bottom-color: rgba(99,102,241,0.06); }
.aum-table tr:last-child td { border-bottom: none; }

/* Alternating row tint */
.aum-table tbody tr:nth-child(even) { background: rgba(99,102,241,0.025); }
[data-theme="light"] .aum-table tbody tr:nth-child(even) { background: rgba(99,102,241,0.02); }

/* Hover — left accent + background lift */
.aum-table tbody tr {
  transition: background 0.15s ease, box-shadow 0.15s ease;
  position: relative;
}
.aum-table tbody tr:hover {
  background: rgba(99,102,241,0.08) !important;
  box-shadow: inset 3px 0 0 rgba(99,102,241,0.5), 0 1px 0 rgba(99,102,241,0.06), 0 -1px 0 rgba(99,102,241,0.06);
}
[data-theme="light"] .aum-table tbody tr:hover {
  background: rgba(99,102,241,0.045) !important;
  box-shadow: inset 3px 0 0 rgba(99,102,241,0.32);
}

/* Last column right-aligned */
.aum-table td:last-child { text-align: right; }

.aum-email {
  font-weight: 500; font-size: 13px; letter-spacing: 0.005em;
  color: rgba(226,232,240,0.92);
  display: flex; align-items: center; gap: 8px;
}
[data-theme="light"] .aum-email { color: #1e293b; }
.aum-you-badge {
  font-size: 9px; padding: 2px 7px; border-radius: 10px; font-weight: 700;
  background: rgba(99,102,241,0.1); color: rgba(165,180,252,0.8);
  border: 1px solid rgba(99,102,241,0.2);
  letter-spacing: 0.05em; text-transform: lowercase;
}
[data-theme="light"] .aum-you-badge { color: #6366f1; background: rgba(99,102,241,0.07); border-color: rgba(99,102,241,0.16); }
.aum-date {
  font-size: 12px; color: rgba(100,116,139,0.5);
  font-variant-numeric: tabular-nums; letter-spacing: 0.02em;
}
[data-theme="light"] .aum-date { color: #94a3b8; }

.aum-role-select {
  padding: 6px 28px 6px 10px; border-radius: 7px; font-size: 12px; font-weight: 500;
  background: rgba(6,9,22,0.6); border: 1px solid rgba(99,102,241,0.2);
  color: rgba(226,232,240,0.92); cursor: pointer; outline: none;
  appearance: none; -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(148,163,184,0.55)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 9px center;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.3);
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
}
[data-theme="light"] .aum-role-select {
  background-color: rgba(248,250,252,0.92); border-color: rgba(99,102,241,0.16); color: #1e293b;
  box-shadow: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(71,85,105,0.5)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
}
.aum-role-select:hover:not(:focus) { border-color: rgba(99,102,241,0.35); background: rgba(8,12,30,0.68); }
[data-theme="light"] .aum-role-select:hover:not(:focus) { background-color: rgba(248,250,252,0.92); border-color: rgba(99,102,241,0.26); }
.aum-role-select:focus {
  border-color: rgba(99,102,241,0.6);
  background: rgba(8,12,30,0.72);
  box-shadow: 0 0 0 3px rgba(99,102,241,0.13), inset 0 1px 2px rgba(0,0,0,0.25);
}
[data-theme="light"] .aum-role-select:focus { border-color: rgba(99,102,241,0.45); background: #fff; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.aum-role-select option { background: #0f1629; color: rgba(226,232,240,0.92); }
[data-theme="light"] .aum-role-select option { background: #fff; color: #1e293b; }
.aum-role-text {
  display: inline-block;
  font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: lowercase;
  padding: 3px 9px; border-radius: 6px;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.18);
  color: rgba(251,191,36,0.78);
}
[data-theme="light"] .aum-role-text { color: #d97706; background: rgba(245,158,11,0.07); border-color: rgba(245,158,11,0.16); }

.aum-remove-btn {
  padding: 5px 13px; border-radius: 7px; font-size: 11px; font-weight: 600;
  background: rgba(239,68,68,0.06);
  border: 1px solid rgba(239,68,68,0.22);
  color: rgba(252,165,165,0.8);
  cursor: pointer; letter-spacing: 0.02em;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}
[data-theme="light"] .aum-remove-btn { color: #ef4444; border-color: rgba(239,68,68,0.22); background: rgba(239,68,68,0.04); box-shadow: none; }
.aum-remove-btn:hover {
  background: rgba(239,68,68,0.12);
  border-color: rgba(239,68,68,0.38);
  color: rgba(252,165,165,0.97);
  box-shadow: 0 0 12px rgba(239,68,68,0.1), inset 0 1px 0 rgba(255,255,255,0.04);
}
[data-theme="light"] .aum-remove-btn:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.35); color: #dc2626; box-shadow: none; }
.aum-remove-btn:active { transform: translateY(0); box-shadow: none; }
`;
